import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_ACCOUNTING_PAYMENT_TYPE_LIST_CONFIGURATION, EMPTY_ACCOUNTING_PAYMENT_TYPE_LIST_CRITERIA, EMPTY_BANK_ACCOUNT_LIST_CONFIGURATION, EMPTY_BANK_ACCOUNT_LIST_CRITERIA, EMPTY_CLIENT_LIST_CONFIGURATION, EMPTY_ClIENT_LIST_CRITERIA, EMPTY_EXCHANGE_ACCOUNT_LIST_CONFIGURATION, EMPTY_EXCHANGE_ACCOUNT_LIST_CRITERIA, EMPTY_INCOME_STATMENT_LIST_CONFIGURATION, EMPTY_INCOME_STATMENT_LIST_CRITERIA, EMPTY_NEWSPAPER_LIST_CONFIGURATION, EMPTY_NEWSPAPER_LIST_CRITERIA, EMPTY_SALE_LIST_CONFIGURATION } from "../constants/accountSettingsConstants";
import { AccountSettingState } from "../objects/accountSettingState";
import { Column } from "../objects/column";
import { IncomeStatmentListCriteria } from "../objects/incomStatmentLIst";
import { Currency } from "../../currency/objects/currency";
import { setSalePreference } from "../utils/setSalePreference";
import { setPurchasePreference } from "../utils/setPurchasePreference";
import { setClientPreference } from "../utils/setClientPreference";
import { setBankAccountPreference } from "../utils/setBankAccountPreference";
import { SaleAccount } from "../objects/saleAccount";
import { PurchaseAccount } from "../objects/purchaseAccount";
import { ClientAccount } from "../objects/clientAccount";
import { NewspaperAccount } from "../objects/newspaperAccount";
import { BankAccount } from "../objects/bankAccount";
import { AccountingCompany } from "../objects/accountingCompany";
import { BankAccountListCriteria } from "../objects/bankAccountLIst ";
import { NewspaperListCriteria } from "../objects/newspaperAccountList";
import { ClientListCriteria } from "../objects/clientAccountLIst";
import { setNewspaperPreference } from "../utils/setNewspaperPreference";
import { ExchangeAccount } from "../objects/exchangeAccount";
import { ExchangeAccountListCriteria } from "../objects/exchangeAccountList";
import { setExchangeAccountPreference } from "../utils/setExchangeAccountPreference";
import { setAccountingPaymentTypePreference } from "../utils/setAccountingPaymentTypePreference";
import { AccountingPaymentType } from "../objects/accountingPaymentType";
import { AccountingPaymentTypeListCriteria } from "../objects/accountingPaymentTypeList";


const initialState: AccountSettingState = {
    sale_list: [],
    sale_list_loaded: false,
    sale_list_refresh: false,
    sale_list_configuration: EMPTY_SALE_LIST_CONFIGURATION,
    sale_list_loading: false,
    sale_list_count: 0,
    sale_list_criteria: EMPTY_INCOME_STATMENT_LIST_CRITERIA,
    sale_is_creating: false,
    sale: null,
    sale_delete: null,

    purchase_list: [],
    purchase_list_loaded: false,
    purchase_list_refresh: false,
    purchase_list_configuration: EMPTY_INCOME_STATMENT_LIST_CONFIGURATION,
    purchase_list_loading: false,
    purchase_list_count: 0,
    purchase_list_criteria: EMPTY_INCOME_STATMENT_LIST_CRITERIA,
    purchase_is_creating: false,
    purchase: null,
    purchase_delete: null,

    client_list: [],
    client_list_loaded: false,
    client_list_refresh: false,
    client_list_configuration: EMPTY_CLIENT_LIST_CONFIGURATION,
    client_list_loading: false,
    client_list_count: 0,
    client_list_criteria: EMPTY_ClIENT_LIST_CRITERIA,
    client_is_creating: false,
    client: null,
    client_delete: null,

    newspaper_list: [],
    newspaper_list_loaded: false,
    newspaper_list_refresh: false,
    newspaper_list_configuration: EMPTY_NEWSPAPER_LIST_CONFIGURATION,
    newspaper_list_loading: false,
    newspaper_list_count: 0,
    newspaper_list_criteria: EMPTY_NEWSPAPER_LIST_CRITERIA,
    newspaper_is_creating: false,
    newspaper: null,
    newspaper_delete: null,

    bank_account_list: [],
    bank_account_list_loaded: false,
    bank_account_list_refresh: false,
    bank_account_list_configuration: EMPTY_BANK_ACCOUNT_LIST_CONFIGURATION,
    bank_account_list_loading: false,
    bank_account_list_count: 0,
    bank_account_list_criteria: EMPTY_BANK_ACCOUNT_LIST_CRITERIA,
    bank_account_is_creating: false,
    bank_account: null,
    bank_account_delete: null,

    accounting_payment_type_list: [],
    accounting_payment_type_list_loaded: false,
    accounting_payment_type_list_refresh: false,
    accounting_payment_type_list_configuration: EMPTY_ACCOUNTING_PAYMENT_TYPE_LIST_CONFIGURATION,
    accounting_payment_type_list_loading: false,
    accounting_payment_type_list_count: 0,
    accounting_payment_type_list_criteria: EMPTY_ACCOUNTING_PAYMENT_TYPE_LIST_CRITERIA,
    accounting_payment_type_is_creating: false,
    accounting_payment_type: null,
    accounting_payment_type_delete: null,

    exchange_account_list: [],
    exchange_account_list_loaded: false,
    exchange_account_list_refresh: false,
    exchange_account_list_configuration: EMPTY_EXCHANGE_ACCOUNT_LIST_CONFIGURATION,
    exchange_account_list_loading: false,
    exchange_account_list_count: 0,
    exchange_account_list_criteria: EMPTY_EXCHANGE_ACCOUNT_LIST_CRITERIA,
    exchange_account_is_creating: false,
    exchange_account: null,
    exchange_account_delete: null,

    currencies: [],

    payment_types: {},
    default_payment_types: {},
    payment_types_loaded: false,
    company: null,

    first_preference_loaded: false,

    refresh_payment_types: false
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => {
    const sale_preference = setSalePreference(action.payload, state.sale_list_configuration.columns);

    const purchase_preference = setPurchasePreference(action.payload, state.purchase_list_configuration.columns);

    const client_preference = setClientPreference(action.payload, state.client_list_configuration.columns);
    const newspaper_preference = setNewspaperPreference(action.payload, state.newspaper_list_configuration.columns);

    const bank_account_preference = setBankAccountPreference(action.payload, state.bank_account_list_configuration.columns);

    const accounting_payment_type_preference = setAccountingPaymentTypePreference(action.payload, state.accounting_payment_type_list_configuration.columns);

    const exchange_account_preference = setExchangeAccountPreference(action.payload, state.exchange_account_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        sale_list_configuration: { ...state.sale_list_configuration, preferences: sale_preference.preferences, columns: sale_preference.columns },
        sale_list_criteria: { ...state.sale_list_criteria, order: sale_preference.order, order_by: sale_preference.order_by },

        purchase_list_configuration: { ...state.purchase_list_configuration, preferences: purchase_preference.preferences, columns: purchase_preference.columns },
        purchase_list_criteria: { ...state.purchase_list_criteria, order: purchase_preference.order, order_by: purchase_preference.order_by },

        client_list_configuration: { ...state.client_list_configuration, preferences: client_preference.preferences, columns: client_preference.columns },
        client_list_criteria: { ...state.client_list_criteria, order: client_preference.order, order_by: client_preference.order_by },
        newspaper_list_configuration: { ...state.newspaper_list_configuration, preferences: newspaper_preference.preferences, columns: newspaper_preference.columns },
        newspaper_list_criteria: { ...state.newspaper_list_criteria, order: newspaper_preference.order, order_by: newspaper_preference.order_by },

        bank_account_list_configuration: { ...state.bank_account_list_configuration, preferences: bank_account_preference.preferences, columns: bank_account_preference.columns },
        bank_account_list_criteria: { ...state.bank_account_list_criteria, order: bank_account_preference.order, order_by: bank_account_preference.order_by },

        accounting_payment_type_list_configuration: { ...state.accounting_payment_type_list_configuration, preferences: accounting_payment_type_preference.preferences, columns: accounting_payment_type_preference.columns },
        accounting_payment_type_list_criteria: { ...state.accounting_payment_type_list_criteria, order: accounting_payment_type_preference.order, order_by: accounting_payment_type_preference.order_by },

        exchange_account_list_configuration: { ...state.exchange_account_list_configuration, preferences: exchange_account_preference.preferences, columns: exchange_account_preference.columns },
        exchange_account_list_criteria: { ...state.exchange_account_list_criteria, order: exchange_account_preference.order, order_by: exchange_account_preference.order_by },
    };
};


//  ------------------------------ sale ----------------------------------- //
const toggleSaleRefreshListReducer = (state: AccountSettingState): AccountSettingState => ({
    ...state, sale_list_refresh: !state.sale_list_refresh, sale_list_loaded: false
});
const addSaleListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, sale_list_configuration: { ...state.sale_list_configuration, preferences: [...state.sale_list_configuration.preferences, ...action.payload] }
});
const setSaleListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, sale_list_configuration: { ...state.sale_list_configuration, preferences: action.payload }
});
const updateSaleListReducer = (state: AccountSettingState, action: PayloadAction<SaleAccount[]>): AccountSettingState => ({
    ...state, sale_list: action.payload, sale_list_loaded: true
});
const updateSaleListCriteriaReducer = (state: AccountSettingState, action: PayloadAction<IncomeStatmentListCriteria>): AccountSettingState => ({
    ...state, sale_list_criteria: action.payload
});
const updateSaleListLoadingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, sale_list_loading: action.payload
});
const updateSaleListCountReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => ({
    ...state, sale_list_count: action.payload
});
const updateSaleIsCreatingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, sale_is_creating: action.payload
});
const toggleSaleColumnReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => {
    const columns = cloneDeep([...state.sale_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, sale_list_configuration: { ...state.sale_list_configuration, columns } };
};
const setSaleColumnsReducer = (state: AccountSettingState, action: PayloadAction<Column[]>): AccountSettingState => ({
    ...state, sale_list_configuration: { ...state.sale_list_configuration, columns: action.payload }
});
const setSaleReducer = (state: AccountSettingState, action: PayloadAction<SaleAccount | null>): AccountSettingState => ({
    ...state, sale: action.payload
});
const setSaleDeleteReducer = (state: AccountSettingState, action: PayloadAction<SaleAccount | null>): AccountSettingState => ({
    ...state, sale_delete: action.payload
});


//  ------------------------------ purchase ----------------------------------- //
const togglePurchaseRefreshListReducer = (state: AccountSettingState): AccountSettingState => ({
    ...state, purchase_list_refresh: !state.purchase_list_refresh, purchase_list_loaded: false
});
const addPurchaseListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, purchase_list_configuration: { ...state.purchase_list_configuration, preferences: [...state.purchase_list_configuration.preferences, ...action.payload] }
});
const setPurchaseListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, purchase_list_configuration: { ...state.purchase_list_configuration, preferences: action.payload }
});
const updatePurchaseListReducer = (state: AccountSettingState, action: PayloadAction<PurchaseAccount[]>): AccountSettingState => ({
    ...state, purchase_list: action.payload, purchase_list_loaded: true
});
const updatePurchaseListCriteriaReducer = (state: AccountSettingState, action: PayloadAction<IncomeStatmentListCriteria>): AccountSettingState => ({
    ...state, purchase_list_criteria: action.payload
});
const updatePurchaseListLoadingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, purchase_list_loading: action.payload
});
const updatePurchaseListCountReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => ({
    ...state, purchase_list_count: action.payload
});
const updatePurchaseIsCreatingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, purchase_is_creating: action.payload
});
const togglePurchaseColumnReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => {
    const columns = cloneDeep([...state.purchase_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, purchase_list_configuration: { ...state.purchase_list_configuration, columns } };
};
const setPurchaseColumnsReducer = (state: AccountSettingState, action: PayloadAction<Column[]>): AccountSettingState => ({
    ...state, purchase_list_configuration: { ...state.purchase_list_configuration, columns: action.payload }
});
const setPurchaseReducer = (state: AccountSettingState, action: PayloadAction<PurchaseAccount | null>): AccountSettingState => ({
    ...state, purchase: action.payload
});
const setPurchaseDeleteReducer = (state: AccountSettingState, action: PayloadAction<PurchaseAccount | null>): AccountSettingState => ({
    ...state, purchase_delete: action.payload
});




//  ------------------------------ client ----------------------------------- //
const toggleClientRefreshListReducer = (state: AccountSettingState): AccountSettingState => ({
    ...state, client_list_refresh: !state.client_list_refresh, client_list_loaded: false
});
const addClientListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, client_list_configuration: { ...state.client_list_configuration, preferences: [...state.client_list_configuration.preferences, ...action.payload] }
});
const setClientListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, client_list_configuration: { ...state.client_list_configuration, preferences: action.payload }
});
const updateClientListReducer = (state: AccountSettingState, action: PayloadAction<ClientAccount[]>): AccountSettingState => ({
    ...state, client_list: action.payload, client_list_loaded: true
});
const updateClientListCriteriaReducer = (state: AccountSettingState, action: PayloadAction<ClientListCriteria>): AccountSettingState => ({
    ...state, client_list_criteria: action.payload
});
const updateClientListLoadingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, client_list_loading: action.payload
});
const updateClientListCountReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => ({
    ...state, client_list_count: action.payload
});
const updateClientIsCreatingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, client_is_creating: action.payload
});
const toggleClientColumnReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => {
    const columns = cloneDeep([...state.client_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, client_list_configuration: { ...state.client_list_configuration, columns } };
};
const setClientColumnsReducer = (state: AccountSettingState, action: PayloadAction<Column[]>): AccountSettingState => ({
    ...state, client_list_configuration: { ...state.client_list_configuration, columns: action.payload }
});
const setClientReducer = (state: AccountSettingState, action: PayloadAction<ClientAccount | null>): AccountSettingState => ({
    ...state, client: action.payload
});
const setClientDeleteReducer = (state: AccountSettingState, action: PayloadAction<ClientAccount | null>): AccountSettingState => ({
    ...state, client_delete: action.payload
});


//  ------------------------------ newspaper ----------------------------------- //
const toggleNewspaperRefreshListReducer = (state: AccountSettingState): AccountSettingState => ({
    ...state, newspaper_list_refresh: !state.newspaper_list_refresh, newspaper_list_loaded: false
});
const addNewspaperListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, newspaper_list_configuration: { ...state.newspaper_list_configuration, preferences: [...state.newspaper_list_configuration.preferences, ...action.payload] }
});
const setNewspaperListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, newspaper_list_configuration: { ...state.newspaper_list_configuration, preferences: action.payload }
});
const updateNewspaperListReducer = (state: AccountSettingState, action: PayloadAction<NewspaperAccount[]>): AccountSettingState => ({
    ...state, newspaper_list: action.payload, newspaper_list_loaded: true
});
const updateNewspaperListCriteriaReducer = (state: AccountSettingState, action: PayloadAction<NewspaperListCriteria>): AccountSettingState => ({
    ...state, newspaper_list_criteria: action.payload
});
const updateNewspaperListLoadingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, newspaper_list_loading: action.payload
});
const updateNewspaperListCountReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => ({
    ...state, newspaper_list_count: action.payload
});
const updateNewspaperIsCreatingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, newspaper_is_creating: action.payload
});
const toggleNewspaperColumnReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => {
    const columns = cloneDeep([...state.newspaper_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, newspaper_list_configuration: { ...state.newspaper_list_configuration, columns } };
};
const setNewspaperColumnsReducer = (state: AccountSettingState, action: PayloadAction<Column[]>): AccountSettingState => ({
    ...state, newspaper_list_configuration: { ...state.newspaper_list_configuration, columns: action.payload }
});
const setNewspaperReducer = (state: AccountSettingState, action: PayloadAction<NewspaperAccount | null>): AccountSettingState => ({
    ...state, newspaper: action.payload
});
const setNewspaperDeleteReducer = (state: AccountSettingState, action: PayloadAction<NewspaperAccount | null>): AccountSettingState => ({
    ...state, newspaper_delete: action.payload
});


//  ------------------------------ bank account ----------------------------------- //
const toggleBankAccountRefreshListReducer = (state: AccountSettingState): AccountSettingState => ({
    ...state, bank_account_list_refresh: !state.bank_account_list_refresh, bank_account_list_loaded: false
});
const addBankAccountListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, bank_account_list_configuration: { ...state.bank_account_list_configuration, preferences: [...state.bank_account_list_configuration.preferences, ...action.payload] }
});
const setBankAccountListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, bank_account_list_configuration: { ...state.bank_account_list_configuration, preferences: action.payload }
});
const updateBankAccountListReducer = (state: AccountSettingState, action: PayloadAction<BankAccount[]>): AccountSettingState => ({
    ...state, bank_account_list: action.payload, bank_account_list_loaded: true
});
const updateBankAccountListCriteriaReducer = (state: AccountSettingState, action: PayloadAction<BankAccountListCriteria>): AccountSettingState => ({
    ...state, bank_account_list_criteria: action.payload
});
const updateBankAccountListLoadingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, bank_account_list_loading: action.payload
});
const updateBankAccountListCountReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => ({
    ...state, bank_account_list_count: action.payload
});
const updateBankAccountIsCreatingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, bank_account_is_creating: action.payload
});
const toggleBankAccountColumnReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => {
    const columns = cloneDeep([...state.bank_account_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, bank_account_list_configuration: { ...state.bank_account_list_configuration, columns } };
};
const setBankAccountColumnsReducer = (state: AccountSettingState, action: PayloadAction<Column[]>): AccountSettingState => ({
    ...state, bank_account_list_configuration: { ...state.bank_account_list_configuration, columns: action.payload }
});
const setBankAccountReducer = (state: AccountSettingState, action: PayloadAction<BankAccount | null>): AccountSettingState => ({
    ...state, bank_account: action.payload
});
const setBankAccountDeleteReducer = (state: AccountSettingState, action: PayloadAction<BankAccount | null>): AccountSettingState => ({
    ...state, bank_account_delete: action.payload
});

//  ------------------------------ accounting payment type ----------------------------------- //
const toggleAccountingPaymentTypeRefreshListReducer = (state: AccountSettingState): AccountSettingState => ({
    ...state, accounting_payment_type_list_refresh: !state.accounting_payment_type_list_refresh, accounting_payment_type_list_loaded: false
});
const addAccountingPaymentTypeListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, accounting_payment_type_list_configuration: { ...state.accounting_payment_type_list_configuration, preferences: [...state.accounting_payment_type_list_configuration.preferences, ...action.payload] }
});
const setAccountingPaymentTypeListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, accounting_payment_type_list_configuration: { ...state.accounting_payment_type_list_configuration, preferences: action.payload }
});
const updateAccountingPaymentTypeListReducer = (state: AccountSettingState, action: PayloadAction<AccountingPaymentType[]>): AccountSettingState => ({
    ...state, accounting_payment_type_list: action.payload, accounting_payment_type_list_loaded: true
});
const updateAccountingPaymentTypeListCriteriaReducer = (state: AccountSettingState, action: PayloadAction<AccountingPaymentTypeListCriteria>): AccountSettingState => ({
    ...state, accounting_payment_type_list_criteria: action.payload
});
const updateAccountingPaymentTypeListLoadingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, accounting_payment_type_list_loading: action.payload
});
const updateAccountingPaymentTypeListCountReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => ({
    ...state, accounting_payment_type_list_count: action.payload
});
const updateAccountingPaymentTypeIsCreatingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, accounting_payment_type_is_creating: action.payload
});
const toggleAccountingPaymentTypeColumnReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => {
    const columns = cloneDeep([...state.accounting_payment_type_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, accounting_payment_type_list_configuration: { ...state.accounting_payment_type_list_configuration, columns } };
};
const setAccountingPaymentTypeColumnsReducer = (state: AccountSettingState, action: PayloadAction<Column[]>): AccountSettingState => ({
    ...state, accounting_payment_type_list_configuration: { ...state.accounting_payment_type_list_configuration, columns: action.payload }
});
const setAccountingPaymentTypeReducer = (state: AccountSettingState, action: PayloadAction<AccountingPaymentType | null>): AccountSettingState => ({
    ...state, accounting_payment_type: action.payload
});
const setAccountingPaymentTypeDeleteReducer = (state: AccountSettingState, action: PayloadAction<AccountingPaymentType | null>): AccountSettingState => ({
    ...state, accounting_payment_type_delete: action.payload
});

//  ------------------------------ exchange account ----------------------------------- //
const toggleExchangeAccountRefreshListReducer = (state: AccountSettingState): AccountSettingState => ({
    ...state, exchange_account_list_refresh: !state.exchange_account_list_refresh, exchange_account_list_loaded: false
});
const addExchangeAccountListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, exchange_account_list_configuration: { ...state.exchange_account_list_configuration, preferences: [...state.exchange_account_list_configuration.preferences, ...action.payload] }
});
const setExchangeAccountListConfigurationPreferenceReducer = (state: AccountSettingState, action: PayloadAction<Preferences[]>): AccountSettingState => ({
    ...state, exchange_account_list_configuration: { ...state.exchange_account_list_configuration, preferences: action.payload }
});
const updateExchangeAccountListReducer = (state: AccountSettingState, action: PayloadAction<ExchangeAccount[]>): AccountSettingState => ({
    ...state, exchange_account_list: action.payload, exchange_account_list_loaded: true
});
const updateExchangeAccountListCriteriaReducer = (state: AccountSettingState, action: PayloadAction<ExchangeAccountListCriteria>): AccountSettingState => ({
    ...state, exchange_account_list_criteria: action.payload
});
const updateExchangeAccountListLoadingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, exchange_account_list_loading: action.payload
});
const updateExchangeAccountListCountReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => ({
    ...state, exchange_account_list_count: action.payload
});
const updateExchangeAccountIsCreatingReducer = (state: AccountSettingState, action: PayloadAction<boolean>): AccountSettingState => ({
    ...state, exchange_account_is_creating: action.payload
});
const toggleExchangeAccountColumnReducer = (state: AccountSettingState, action: PayloadAction<number>): AccountSettingState => {
    const columns = cloneDeep([...state.exchange_account_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, exchange_account_list_configuration: { ...state.exchange_account_list_configuration, columns } };
};
const setExchangeAccountColumnsReducer = (state: AccountSettingState, action: PayloadAction<Column[]>): AccountSettingState => ({
    ...state, exchange_account_list_configuration: { ...state.exchange_account_list_configuration, columns: action.payload }
});
const setExchangeAccountReducer = (state: AccountSettingState, action: PayloadAction<ExchangeAccount | null>): AccountSettingState => ({
    ...state, exchange_account: action.payload
});
const setExchangeAccountDeleteReducer = (state: AccountSettingState, action: PayloadAction<ExchangeAccount | null>): AccountSettingState => ({
    ...state, exchange_account_delete: action.payload
});

// --------------- currencies ----------------------- //
const setCurrenciesReducer = (state: AccountSettingState, action: PayloadAction<Currency[]>): AccountSettingState => ({
    ...state, currencies: action.payload
});


const setDefaultPaymentTypesReducer = (state: AccountSettingState, action: PayloadAction<Record<string, string>>): AccountSettingState => ({
    ...state, default_payment_types: action.payload, payment_types_loaded: true
});
const setPaymentTypesReducer = (state: AccountSettingState, action: PayloadAction<Record<string, string>>): AccountSettingState => ({
    ...state, payment_types: action.payload, payment_types_loaded: true
});
const toggleRefreshPaymentTypesReducer = (state: AccountSettingState): AccountSettingState => ({
    ...state, refresh_payment_types: !state.refresh_payment_types
});

const setCompanyReducer = (state: AccountSettingState, action: PayloadAction<null | any | AccountingCompany>): AccountSettingState => ({
    ...state, company: action.payload
});

export const accountSetting = createSlice({
    name: 'account_setting',
    initialState,
    reducers: {
        toggleSaleRefreshList: toggleSaleRefreshListReducer,
        addSaleListConfigurationPreference: addSaleListConfigurationPreferenceReducer,
        setSaleListConfigurationPreference: setSaleListConfigurationPreferenceReducer,
        updateSaleList: updateSaleListReducer,
        updateSaleListCriteria: updateSaleListCriteriaReducer,
        updateSaleListLoading: updateSaleListLoadingReducer,
        updateSaleListCount: updateSaleListCountReducer,
        updateSaleIsCreating: updateSaleIsCreatingReducer,
        setSaleColumns: setSaleColumnsReducer,
        toggleSaleColumn: toggleSaleColumnReducer,
        setSale: setSaleReducer,
        setSaleDelete: setSaleDeleteReducer,

        togglePurchaseRefreshList: togglePurchaseRefreshListReducer,
        addPurchaseListConfigurationPreference: addPurchaseListConfigurationPreferenceReducer,
        setPurchaseListConfigurationPreference: setPurchaseListConfigurationPreferenceReducer,
        updatePurchaseList: updatePurchaseListReducer,
        updatePurchaseListCriteria: updatePurchaseListCriteriaReducer,
        updatePurchaseListLoading: updatePurchaseListLoadingReducer,
        updatePurchaseListCount: updatePurchaseListCountReducer,
        updatePurchaseIsCreating: updatePurchaseIsCreatingReducer,
        setPurchaseColumns: setPurchaseColumnsReducer,
        togglePurchaseColumn: togglePurchaseColumnReducer,
        setPurchase: setPurchaseReducer,
        setPurchaseDelete: setPurchaseDeleteReducer,

        toggleClientRefreshList: toggleClientRefreshListReducer,
        addClientListConfigurationPreference: addClientListConfigurationPreferenceReducer,
        setClientListConfigurationPreference: setClientListConfigurationPreferenceReducer,
        updateClientList: updateClientListReducer,
        updateClientListCriteria: updateClientListCriteriaReducer,
        updateClientListLoading: updateClientListLoadingReducer,
        updateClientListCount: updateClientListCountReducer,
        updateClientIsCreating: updateClientIsCreatingReducer,
        setClientColumns: setClientColumnsReducer,
        toggleClientColumn: toggleClientColumnReducer,
        setClient: setClientReducer,
        setClientDelete: setClientDeleteReducer,

        toggleNewspaperRefreshList: toggleNewspaperRefreshListReducer,
        addNewspaperListConfigurationPreference: addNewspaperListConfigurationPreferenceReducer,
        setNewspaperListConfigurationPreference: setNewspaperListConfigurationPreferenceReducer,
        updateNewspaperList: updateNewspaperListReducer,
        updateNewspaperListCriteria: updateNewspaperListCriteriaReducer,
        updateNewspaperListLoading: updateNewspaperListLoadingReducer,
        updateNewspaperListCount: updateNewspaperListCountReducer,
        updateNewspaperIsCreating: updateNewspaperIsCreatingReducer,
        setNewspaperColumns: setNewspaperColumnsReducer,
        toggleNewspaperColumn: toggleNewspaperColumnReducer,
        setNewspaper: setNewspaperReducer,
        setNewspaperDelete: setNewspaperDeleteReducer,

        toggleBankAccountRefreshList: toggleBankAccountRefreshListReducer,
        addBankAccountListConfigurationPreference: addBankAccountListConfigurationPreferenceReducer,
        setBankAccountListConfigurationPreference: setBankAccountListConfigurationPreferenceReducer,
        updateBankAccountList: updateBankAccountListReducer,
        updateBankAccountListCriteria: updateBankAccountListCriteriaReducer,
        updateBankAccountListLoading: updateBankAccountListLoadingReducer,
        updateBankAccountListCount: updateBankAccountListCountReducer,
        updateBankAccountIsCreating: updateBankAccountIsCreatingReducer,
        setBankAccountColumns: setBankAccountColumnsReducer,
        toggleBankAccountColumn: toggleBankAccountColumnReducer,
        setBankAccount: setBankAccountReducer,
        setBankAccountDelete: setBankAccountDeleteReducer,

        toggleAccountingPaymentTypeRefreshList: toggleAccountingPaymentTypeRefreshListReducer,
        addAccountingPaymentTypeListConfigurationPreference: addAccountingPaymentTypeListConfigurationPreferenceReducer,
        setAccountingPaymentTypeListConfigurationPreference: setAccountingPaymentTypeListConfigurationPreferenceReducer,
        updateAccountingPaymentTypeList: updateAccountingPaymentTypeListReducer,
        updateAccountingPaymentTypeListCriteria: updateAccountingPaymentTypeListCriteriaReducer,
        updateAccountingPaymentTypeListLoading: updateAccountingPaymentTypeListLoadingReducer,
        updateAccountingPaymentTypeListCount: updateAccountingPaymentTypeListCountReducer,
        updateAccountingPaymentTypeIsCreating: updateAccountingPaymentTypeIsCreatingReducer,
        setAccountingPaymentTypeColumns: setAccountingPaymentTypeColumnsReducer,
        toggleAccountingPaymentTypeColumn: toggleAccountingPaymentTypeColumnReducer,
        setAccountingPaymentType: setAccountingPaymentTypeReducer,
        setAccountingPaymentTypeDelete: setAccountingPaymentTypeDeleteReducer,

        toggleExchangeAccountRefreshList: toggleExchangeAccountRefreshListReducer,
        addExchangeAccountListConfigurationPreference: addExchangeAccountListConfigurationPreferenceReducer,
        setExchangeAccountListConfigurationPreference: setExchangeAccountListConfigurationPreferenceReducer,
        updateExchangeAccountList: updateExchangeAccountListReducer,
        updateExchangeAccountListCriteria: updateExchangeAccountListCriteriaReducer,
        updateExchangeAccountListLoading: updateExchangeAccountListLoadingReducer,
        updateExchangeAccountListCount: updateExchangeAccountListCountReducer,
        updateExchangeAccountIsCreating: updateExchangeAccountIsCreatingReducer,
        setExchangeAccountColumns: setExchangeAccountColumnsReducer,
        toggleExchangeAccountColumn: toggleExchangeAccountColumnReducer,
        setExchangeAccount: setExchangeAccountReducer,
        setExchangeAccountDelete: setExchangeAccountDeleteReducer,

        setCurrencies: setCurrenciesReducer,
        setPaymentTypes: setPaymentTypesReducer,
        setDefaultPaymentTypes: setDefaultPaymentTypesReducer,
        toggleRefreshPaymentTypes: toggleRefreshPaymentTypesReducer,
        setCompany: setCompanyReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    toggleSaleRefreshList,
    addSaleListConfigurationPreference,
    setSaleListConfigurationPreference,
    updateSaleList,
    updateSaleListCriteria,
    updateSaleListLoading,
    updateSaleListCount,
    updateSaleIsCreating,
    setSaleColumns,
    toggleSaleColumn,
    setSale,
    setSaleDelete,

    togglePurchaseRefreshList,
    addPurchaseListConfigurationPreference,
    setPurchaseListConfigurationPreference,
    updatePurchaseList,
    updatePurchaseListCriteria,
    updatePurchaseListLoading,
    updatePurchaseListCount,
    updatePurchaseIsCreating,
    setPurchaseColumns,
    togglePurchaseColumn,
    setPurchase,
    setPurchaseDelete,

    toggleClientRefreshList,
    addClientListConfigurationPreference,
    setClientListConfigurationPreference,
    updateClientList,
    updateClientListCriteria,
    updateClientListLoading,
    updateClientListCount,
    updateClientIsCreating,
    setClientColumns,
    toggleClientColumn,
    setClient,
    setClientDelete,

    toggleNewspaperRefreshList,
    addNewspaperListConfigurationPreference,
    setNewspaperListConfigurationPreference,
    updateNewspaperList,
    updateNewspaperListCriteria,
    updateNewspaperListLoading,
    updateNewspaperListCount,
    updateNewspaperIsCreating,
    setNewspaperColumns,
    toggleNewspaperColumn,
    setNewspaper,
    setNewspaperDelete,

    toggleBankAccountRefreshList,
    addBankAccountListConfigurationPreference,
    setBankAccountListConfigurationPreference,
    updateBankAccountList,
    updateBankAccountListCriteria,
    updateBankAccountListLoading,
    updateBankAccountListCount,
    updateBankAccountIsCreating,
    setBankAccountColumns,
    toggleBankAccountColumn,
    setBankAccount,
    setBankAccountDelete,

    toggleAccountingPaymentTypeRefreshList,
    addAccountingPaymentTypeListConfigurationPreference,
    setAccountingPaymentTypeListConfigurationPreference,
    updateAccountingPaymentTypeList,
    updateAccountingPaymentTypeListCriteria,
    updateAccountingPaymentTypeListLoading,
    updateAccountingPaymentTypeListCount,
    updateAccountingPaymentTypeIsCreating,
    setAccountingPaymentTypeColumns,
    toggleAccountingPaymentTypeColumn,
    setAccountingPaymentType,
    setAccountingPaymentTypeDelete,

    toggleExchangeAccountRefreshList,
    addExchangeAccountListConfigurationPreference,
    setExchangeAccountListConfigurationPreference,
    updateExchangeAccountList,
    updateExchangeAccountListCriteria,
    updateExchangeAccountListLoading,
    updateExchangeAccountListCount,
    updateExchangeAccountIsCreating,
    setExchangeAccountColumns,
    toggleExchangeAccountColumn,
    setExchangeAccount,
    setExchangeAccountDelete,

    setPaymentTypes,
    setDefaultPaymentTypes,
    toggleRefreshPaymentTypes,
    setCurrencies,
    setCompany
} = accountSetting.actions;